import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronRight,
  ClipboardCheck,
  ExternalLink,
  FileCheck2,
  Loader2,
  Newspaper,
  ShieldCheck,
  X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useApproveArticle,
  useApproveDocument,
  usePendingArticles,
  usePendingDocuments,
  useRejectArticle,
  useRejectDocument,
} from "@/hooks/useVerification";
import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import { formatDateTime } from "@/lib/format";
import type { ArticleRow, DocumentRow, WorkflowState } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * /verification — single page surfacing every item waiting on the
 * current actor's decision.
 *
 *   Director sees their own-department `pending_review` queue.
 *   CEO    sees both queues org-wide.
 *   Admin  sees both queues across every department.
 *
 * Each row has Approve / Reject buttons; the API decides what the
 * transition actually means based on stage + actor role.
 */
export default function Verification() {
  const { data: user } = useCurrentUser();
  const docs = usePendingDocuments();
  const arts = usePendingArticles();

  // `count` is the total across all pages (DRF). `results.length` would
  // only reflect the first page and undercount once the queue paginates.
  const docCount = docs.data?.count ?? 0;
  const artCount = arts.data?.count ?? 0;
  const total = docCount + artCount;

  if (!user) return null;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="space-y-3">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1">
            {/* Title + prominent pending counter inline. The previous
                small outline badge in the corner was easy to miss; this
                amber pill sits next to the H1 so a Director can see
                the queue depth without scanning. */}
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">Verification queue</h1>
              {!docs.isLoading && !arts.isLoading && total > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1 text-sm font-bold text-white shadow-soft animate-in fade-in slide-in-from-left-1"
                  aria-label={`${total} items waiting on you`}
                  title={`${total} item${total === 1 ? "" : "s"} waiting`}
                >
                  <span className="tabular-nums">{total}</span>
                  <span className="text-xs font-medium opacity-90">
                    pending
                  </span>
                </span>
              )}
              {!docs.isLoading && !arts.isLoading && total === 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  <Check className="h-3 w-3" />All clear
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {gateBlurb(user.role)}
            </p>
          </div>
        </div>

        {/* CEO-specific legend so they know what the two stages mean. */}
        {user.is_ceo_level && (
          <div className="grid gap-3 rounded-lg border border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <StageBadge state="pending_review" label="Director review" />
              <span>
                User-submitted, no Director has cleared it yet. You can fast-track or
                let the Director handle it.
              </span>
            </div>
            <div className="flex items-start gap-2">
              <StageBadge state="pending_ceo_review" label="CEO sign-off" />
              <span>
                Already cleared by a Director — only your sign-off stands between this
                and the public portal.
              </span>
            </div>
          </div>
        )}
      </header>

      {/* ---------- Documents ---------- */}
      <Section
        title="Documents"
        description="Files queued for approval."
        count={docCount}
        loading={docs.isLoading}
        icon={FileCheck2}
      >
        {docs.data && docs.data.results.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {docs.data.results.map((d) => (
              <DocumentItem key={d.id} doc={d} />
            ))}
          </ul>
        ) : !docs.isLoading ? (
          <EmptyState icon={ClipboardCheck} message="No documents waiting on you." />
        ) : null}
      </Section>

      {/* ---------- Articles ---------- */}
      <Section
        title="Articles"
        description="Editorial content queued for approval."
        count={artCount}
        loading={arts.isLoading}
        icon={Newspaper}
      >
        {arts.data && arts.data.results.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {arts.data.results.map((a) => (
              <ArticleItem key={a.id} article={a} />
            ))}
          </ul>
        ) : !arts.isLoading ? (
          <EmptyState icon={ClipboardCheck} message="No articles waiting on you." />
        ) : null}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  description: string;
  count: number;
  loading: boolean;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

function Section({ title, description, count, loading, icon: Icon, children }: SectionProps) {
  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b border-border/60 bg-muted/30 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-card text-muted-foreground">
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
        {!loading && (
          // Number is the at-a-glance signal — amber pill when there's
          // backlog, neutral when zero. Tabular nums so multi-digit
          // counts don't shift the layout.
          <span
            className={cn(
              "inline-flex h-7 min-w-[2rem] items-center justify-center rounded-full px-2.5 text-sm font-bold tabular-nums",
              count > 0
                ? "bg-amber-500 text-white shadow-sm"
                : "bg-muted text-muted-foreground",
            )}
            aria-label={`${count} item${count === 1 ? "" : "s"} in ${title}`}
          >
            {count}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Per-row items
// ---------------------------------------------------------------------------

function DocumentItem({ doc }: { doc: DocumentRow }) {
  const [pending, setPending] = useState<"approve" | "reject" | null>(null);
  const approve = useApproveDocument();
  const reject  = useRejectDocument();
  const confirm = useConfirm();

  const style = FILE_TYPE_STYLES[doc.file_type];
  const Icon  = style.icon;

  function onApprove() {
    setPending("approve");
    approve.mutate(doc.id, { onSettled: () => setPending(null) });
  }
  async function onReject() {
    const ok = await confirm({
      title: `Reject "${doc.title}"?`,
      description: "The owner will be notified.",
      confirmLabel: "Reject",
      destructive: true,
    });
    if (!ok) return;
    setPending("reject");
    reject.mutate(doc.id, { onSettled: () => setPending(null) });
  }

  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {/* Icon — clickable too so reviewers can hit either the icon
            or the title to open the doc. */}
        <Link
          to={`/library/${doc.id}`}
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-transform hover:scale-105",
            style.chipClass,
          )}
          aria-label={`Open "${doc.title}"`}
          title="Open document to review"
        >
          <Icon className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <Link
            to={`/library/${doc.id}`}
            className="group inline-flex max-w-full items-center gap-1.5 truncate text-sm font-medium text-foreground hover:text-primary"
            title="Open document to review"
          >
            <span className="truncate group-hover:underline">{doc.title}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
          </Link>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{doc.owner_name || "—"}</span>
            <span>·</span>
            <span>{formatDateTime(doc.created_at)}</span>
            <ChevronRight className="h-3 w-3 opacity-40" />
            <DestinationBadge requested={doc.requested_visibility} />
          </div>
        </div>
        <StageBadge state={doc.workflow_state} label={doc.workflow_state_display} />
      </div>

      <ActionButtons
        onApprove={onApprove}
        onReject={onReject}
        pending={pending}
        viewHref={`/library/${doc.id}`}
      />
    </li>
  );
}

function ArticleItem({ article }: { article: ArticleRow }) {
  const [pending, setPending] = useState<"approve" | "reject" | null>(null);
  const approve = useApproveArticle();
  const reject  = useRejectArticle();
  const confirm = useConfirm();

  function onApprove() {
    setPending("approve");
    approve.mutate(article.slug, { onSettled: () => setPending(null) });
  }
  async function onReject() {
    const ok = await confirm({
      title: `Reject "${article.title}"?`,
      description: "The owner will be notified.",
      confirmLabel: "Reject",
      destructive: true,
    });
    if (!ok) return;
    setPending("reject");
    reject.mutate(article.slug, { onSettled: () => setPending(null) });
  }

  const detailHref = `/articles/${article.slug}`;
  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Link
          to={detailHref}
          className="shrink-0 transition-transform hover:scale-105"
          aria-label={`Open "${article.title}"`}
          title="Open article to review"
        >
          {article.image_url ? (
            <img
              src={article.image_url}
              alt=""
              className="h-10 w-10 rounded-lg object-cover ring-1 ring-border"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
              <Newspaper className="h-4 w-4" />
            </div>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <Link
            to={detailHref}
            className="group inline-flex max-w-full items-center gap-1.5 truncate text-sm font-medium text-foreground hover:text-primary"
            title="Open article to review"
          >
            <span className="truncate group-hover:underline">{article.title}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
          </Link>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{article.owner_name || "—"}</span>
            <span>·</span>
            <span>{formatDateTime(article.created_at)}</span>
            <ChevronRight className="h-3 w-3 opacity-40" />
            <DestinationBadge requested={article.requested_visibility} />
          </div>
        </div>
        <StageBadge state={article.status} label={article.status_display} />
      </div>

      <ActionButtons
        onApprove={onApprove}
        onReject={onReject}
        pending={pending}
        viewHref={detailHref}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function ActionButtons({
  onApprove, onReject, pending, viewHref,
}: {
  onApprove: () => void;
  onReject: () => void;
  pending: "approve" | "reject" | null;
  /** When provided, renders a leading "View" button that deep-links
   *  to the item's detail page so the reviewer can read it before
   *  approving or rejecting. */
  viewHref?: string;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      {viewHref && (
        <Button asChild size="sm" variant="outline" disabled={!!pending}>
          <Link to={viewHref}>
            <ExternalLink className="h-4 w-4" />
            View
          </Link>
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        className="border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:bg-rose-500/15 hover:text-rose-800 dark:text-rose-300"
        onClick={onReject}
        disabled={!!pending}
      >
        {pending === "reject"
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <X className="h-4 w-4" />}
        Reject
      </Button>
      <Button
        size="sm"
        onClick={onApprove}
        disabled={!!pending}
      >
        {pending === "approve"
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <Check className="h-4 w-4" />}
        Approve
      </Button>
    </div>
  );
}

function StageBadge({ state, label }: { state: WorkflowState; label?: string }) {
  const text = label ?? (state === "pending_ceo_review" ? "Pending CEO" : "Pending Director");
  const className = state === "pending_ceo_review"
    ? "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30"
    : "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30";
  return (
    <Badge variant="outline" className={cn("hidden border font-medium md:inline-flex", className)}>
      {text}
    </Badge>
  );
}

function DestinationBadge({ requested }: { requested: WorkflowState | null }) {
  const isPublic = !requested || requested === "published";
  return (
    <span className={cn(
      "rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
      isPublic
        ? "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        : "border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300",
    )}>
      → {isPublic ? "Public portal" : "Staff portal"}
    </span>
  );
}

function EmptyState({
  icon: Icon,
  message,
}: {
  icon: React.ComponentType<{ className?: string }>;
  message: string;
}) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function gateBlurb(role: string): string {
  switch (role) {
    case "admin":    return "Everything queued across every department, every stage.";
    case "ceo":      return "Items already cleared by a Director, plus anything you want to fast-track.";
    case "director": return "Submissions from your department waiting for your first-pass approval.";
    default:         return "You don't have approval rights — this page is empty for you.";
  }
}
